import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import FlavorImage from './FlavorImage';
import getProductImagePath from '../utils/productImages';
import './ModalProductCard.scss';

function formatPrice(price) {
  if (typeof price !== 'number') return '';
  return price % 1 === 0 
    ? `$${price}` 
    : `$${price.toFixed(2)}`;
}


const ModalProductCard = ({
  product,
  config,
  canonicalProduct,
  onSale,
  hasValidUrl,
  onViewDetails,
  small,
  distance,
  city,
  state
}) => {
  const imagePath = useMemo(() => getProductImagePath(canonicalProduct), [canonicalProduct]);
  const productBrand = canonicalProduct?.brand || '';
  const productDisplayName = canonicalProduct?.displayName || product.name;
  const productTHC = canonicalProduct?.thc || '';
  let stockIndicator = 'in-stock';
  if (config?.currentStock <= 0) stockIndicator = 'out-of-stock';
  else if (config?.currentStock <= 5) stockIndicator = 'low-stock';

  // Menu type indicators
  const isMedical = product.menuTypes?.includes('medical');
  const isRecreational = product.menuTypes?.includes('recreational');

  return (
    <div
      className={`modal-product-card clean-design product-card${small ? ' small' : ''}`}
      style={{
        borderRadius: 16,
        boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
        padding: '14px 8px',
        background: '#fff',
        position: 'relative',
        width: 190, /* Match the CSS grid minmax value */
        minWidth: 190,
        maxWidth: 190,
        minHeight: 240, /* Consistent height */
        margin: '0 auto', /* Center card in grid cell */
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
        overflow: 'hidden',
        boxSizing: 'border-box', /* Include padding in width calculation */
        pointerEvents: 'auto' /* Ensure click events work properly */
      }}
      onClick={(e) => {
        // Only stop propagation if this click is directly on the card element
        // and not on a child element (like the button)
        if (e.currentTarget === e.target) {
          e.stopPropagation();
        }
      }}
    >
      {/* No store-info-corner for modal */}
      {isMedical && (
        <div className="menu-type-indicator medical">MED</div>
      )}
      {isRecreational && (
        <div className="menu-type-indicator recreational">REC</div>
      )}
      <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 8}}>
        <FlavorImage src={imagePath} alt={`${productDisplayName} flavor`} style={{width: 56, height: 56, objectFit: 'contain', marginBottom: 6}} />
        <div className="product-name" style={{fontSize: 16, fontWeight: 800, marginBottom: 2, textAlign: 'center', letterSpacing: '-0.5px'}}>{productDisplayName}</div>
        <div className="product-subtitle" style={{fontSize: 12, color: '#555', fontWeight: 500, marginBottom: 4, textAlign: 'center'}}>
          {productBrand && productTHC && (`${productBrand} • ${productTHC}`)}
        </div>
        <div className="product-price" style={{fontSize: 16, fontWeight: 700, marginBottom: 6, textAlign: 'center'}}>
          {onSale ? (
            <>
              <span className="sale-price" style={{color: '#e53935', fontWeight: 700}}>{formatPrice(config.salePrice)}</span>
              <span className="original-price" style={{textDecoration: 'line-through', color: '#aaa', marginLeft: 6}}>{formatPrice(config.normalPrice)}</span>
            </>
          ) : (
            <span className="regular-price">{formatPrice(config?.normalPrice)}</span>
          )}
        </div>
        <div className="product-meta" style={{width: '100%', marginBottom: 8}}>
          <div className="stock-distance-info" style={{display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 500, color: '#444'}}>
            <span className={`stock-info${stockIndicator === 'low-stock' ? ' low-stock-text' : ''}`}> 
              <span style={{fontWeight: 600, color: stockIndicator === 'low-stock' ? '#e67c00' : '#444', marginLeft: 2}}>
                {!config ? (
                  "Stock unknown"
                ) : config.currentStock <= 0 ? (
                  "Out of Stock"
                ) : config.currentStock <= 5 ? (
                  `${config.currentStock} left`
                ) : (
                  `${config.currentStock > 20 ? '20+' : config.currentStock} left`
                )}
              </span>
            </span>
            <span className="separator">|</span>
            <span className="distance-info">
              <span style={{fontWeight: 600}}>
                {typeof distance === 'number' && isFinite(distance) ? (
                  <>{distance.toFixed(2)} mi</>
                ) : (
                  city && state ? <>{city}, {state}</> : ''
                )}
              </span>
            </span>
          </div>
        </div>
      </div>
      {hasValidUrl ? (
        <a
          className="view-details-button"
          aria-label={`View ${productDisplayName} product`}
          onClick={(e) => {
            // Ensure event doesn't bubble up to parent card
            e.preventDefault();
            e.stopPropagation();
            
            if (typeof onViewDetails === 'function') {
              onViewDetails(e);
            }
          }}
          tabIndex={0}
          role="button"
        >
          <span>View product</span>
          <svg className="view-details-icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
            <path d="M7.5 4.5H5.75A2.25 2.25 0 0 0 3.5 6.75v7.5a2.25 2.25 0 0 0 2.25 2.25h7.5a2.25 2.25 0 0 0 2.25-2.25V12.5M11 3.5h5.5V9M16.25 3.75 9 11" />
          </svg>
        </a>
      ) : (
        <span className="view-details-button disabled" title="This product does not have a valid URL">
          Link unavailable
        </span>
      )}
    </div>
  );
};

ModalProductCard.propTypes = {
  product: PropTypes.object.isRequired,
  config: PropTypes.object,
  canonicalProduct: PropTypes.object,
  onSale: PropTypes.bool,
  hasValidUrl: PropTypes.bool,
  onViewDetails: PropTypes.func,
  small: PropTypes.bool,
  distance: PropTypes.number,
  city: PropTypes.string,
  state: PropTypes.string
};

export default ModalProductCard;
